import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "../../shared/types";
import { API_ENDPOINTS } from "../../shared/config";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      setLoading(true);
      
      // Check if we're in development (same-domain) or production (cross-domain)
      const isDevelopment = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      
      // For production (cross-domain), use localStorage token
      // For development (same-domain), rely on cookies
      if (!isDevelopment) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(API_ENDPOINTS.auth.verify, {
        method: "GET",
        credentials: "include", // Always include cookies
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.user) {
          setUser(data.data.user);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Redirect to login endpoint
    window.location.href = API_ENDPOINTS.auth.login;
  };

  const logout = async () => {
    try {
      await fetch(API_ENDPOINTS.auth.logout, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      // Clear localStorage token (production only)
      const isDevelopment = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      if (!isDevelopment) {
        localStorage.removeItem('auth_token');
      }
      setUser(null);
      // Redirect to login page
      window.location.href = "/login";
    }
  };

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
